import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Check } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { type PropertyFeaturesFragment } from '@graphql/__generated__/graphql';
import { equipmentConditionalFields } from '@modules/_shared/components/property-form/forms-definitions/propertyFeaturesDefinition';
import type { PropertyTypeLight } from '@modules/_shared/components/property-form/propertyFormQueries';
import { type Translate, useLocale } from '@shared/hooks/locale';

export type TPropertyFeatures = PropertyFeaturesFragment & {
  __property_type?: PropertyTypeLight;
};

export const PROPERTY_FEATURES_FRAGMENT = gql(/* GraphQL */ `
  fragment PropertyFeatures on properties {
    is_luxury
    is_child_friendly
    is_wheelchair_accessible
    is_new_development
    has_gym
    has_view
    has_attic
    has_sauna
    has_cellar
    has_elevator
    has_fireplace
    has_storage_room
    has_swimming_pool
    has_wellness_area
    has_photovoltaic_panels
  }
`);

interface PropertyFeaturesProps {
  property: TPropertyFeatures;
}

type PropertyFeaturesFields = {
  name: keyof PropertyFeaturesFragment;
  label: string;
  shouldRender?: (property: TPropertyFeatures) => boolean;
};

const getPropertyFeaturesFieldsDefinition = (t: Translate) => {
  const fields: PropertyFeaturesFields[] = [
    {
      name: 'has_gym',
      label: t('Gym'),
    },
    {
      name: 'has_view',
      label: t('Unobstructed view'),
    },
    {
      name: 'has_attic',
      label: t('Attic'),
    },
    {
      name: 'has_sauna',
      label: t('Sauna'),
    },
    {
      name: 'has_cellar',
      label: t('Cellar'),
    },
    {
      name: 'has_elevator',
      label: t('Elevator'),
    },
    {
      name: 'has_fireplace',
      label: t('Fireplace'),
    },
    {
      name: 'has_storage_room',
      label: t('Storage room'),
    },
    {
      name: 'has_swimming_pool',
      label: t('Swimming pool'),
    },
    {
      name: 'has_wellness_area',
      label: t('Wellness area'),
    },
    {
      name: 'has_photovoltaic_panels',
      label: t('Photovoltaic panels'),
    },
    {
      name: 'is_luxury',
      label: t('Luxury asset'),
    },
    {
      name: 'is_child_friendly',
      label: t('Child friendly'),
    },
    {
      name: 'is_wheelchair_accessible',
      label: t('Wheelchair accessible'),
    },
    {
      name: 'is_new_development',
      label: t('New development'),
    },
  ];

  return fields.map(field => {
    const shouldRender = equipmentConditionalFields.get(field.name as any);

    return { ...field, shouldRender } as PropertyFeaturesFields;
  });
};

export const PropertyFeatures: React.FC<PropertyFeaturesProps> = ({
  property,
}) => {
  const { t } = useLocale();

  const features = getPropertyFeaturesFieldsDefinition(t);
  const nbDisplayedFields = features.reduce((acc, field) => {
    if (
      (field.shouldRender && !field.shouldRender(property)) ||
      !property[field.name]
    ) {
      return acc;
    }

    return acc + 1;
  }, 0);

  return (
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, mt: 1 }}>
        {t('equipmentAndFeatures')}
      </Typography>
      {nbDisplayedFields === 0 ? (
        <Typography variant="body2" sx={{ my: 0.5 }}>
          {t('None')}
        </Typography>
      ) : (
        <List
          dense
          sx={{
            columnCount: { xs: 1, md: nbDisplayedFields > 1 ? 2 : 1 },
            columnGap: 5,
            pt: 0,
            pb: 0.5,
          }}
        >
          {features.map(
            field =>
              (!field.shouldRender || field.shouldRender(property)) &&
              !!property[field.name] && (
                <ListItem
                  sx={{ width: 'auto' }}
                  disableGutters
                  key={field.name}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                    <Check size={16} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="body2">{field.label}</Typography>
                  </ListItemText>
                </ListItem>
              ),
          )}
        </List>
      )}
    </Box>
  );
};
